import { debounceDelayMedium } from "@teamprevent/common/filtering/constants";
import { debounce } from "lodash";
import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Button, Grid, Table, InputOnChangeData, Input, TableCell } from "semantic-ui-react";

import { customerApi } from "../../customer/customerApi";

import { CustomerUserListModel } from "../model";
import { SelectorState } from "../selectors";

import { emailAddressSelector, firstNameSelector, surnameSelector } from "./searchSelectors";

import { Link, Query, replace } from "redux-little-router";

export const All: React.FunctionComponent = () => {
    const path = useSelector((state: SelectorState) => state.router.pathname);
    const customerId = useSelector((state: SelectorState) => state.router.params?.customerId);
    const firstNameFromQuery = useSelector(firstNameSelector);
    const surnameFromQuery = useSelector(surnameSelector);
    const emailAddressFromQuery = useSelector(emailAddressSelector);

    const dispatch = useDispatch();

    const [firstName, setFirstName] = React.useState<string | undefined>(firstNameFromQuery);
    const [surname, setSurname] = React.useState<string | undefined>(surnameFromQuery);
    const [emailAddress, setEmailAddress] = React.useState<string | undefined>(emailAddressFromQuery);
    const [customerUsers, setCustomerUsers] = React.useState<CustomerUserListModel[] | undefined>(undefined);

    const getCustomerUsersDebounced = React.useMemo(() => {
        return debounce(async (
            _customerId: string | undefined,
            params: { firstName: string | undefined; surname: string | undefined; emailAddress: string | undefined }) =>
        {
            if (_customerId) {
                const users = await customerApi.customerUsers(_customerId, params);
                setCustomerUsers(users);
            }
        }, debounceDelayMedium);
    }, []);

    const setQueryParams = React.useCallback(() => {
        const query: Query = {};

        // Clear the query params first before setting new ones
        dispatch(replace({ pathname: "", query }, { persistQuery: false }));

        if (firstName) {
            query.firstName = firstName;
        }

        if (surname) {
            query.surname = surname;
        }
        if (emailAddress) {
            query.emailAddress = emailAddress;
        }

        dispatch(replace({ pathname: "", query }, { persistQuery: true }));
    }, [dispatch, emailAddress, firstName, surname]);

    React.useEffect(() => {
        setQueryParams();
        getCustomerUsersDebounced(customerId, { firstName, surname, emailAddress });
    }, [firstName, surname, emailAddress, getCustomerUsersDebounced, customerId, setQueryParams]);

    const handleFirstNameChange = React.useCallback((_event: React.SyntheticEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setFirstName(data.value);
    }, []);

    const handleSurnameChange = React.useCallback((_event: React.SyntheticEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setSurname(data.value);
    }, []);
    const handleEmailAddressChange = React.useCallback((_event: React.SyntheticEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setEmailAddress(data.value);
    }, []);

    if (!customerUsers)
    {
        return null;
    }

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column width={14}>
                    <h1>Customer Users</h1>
                </Grid.Column>
                <Grid.Column width={2} textAlign="right">
                    <Button icon="add" content="Create" as={Link} href={`${path}/create`} />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Email Address</Table.HeaderCell>
                                <Table.HeaderCell>First Name</Table.HeaderCell>
                                <Table.HeaderCell>Surname</Table.HeaderCell>
                                <Table.HeaderCell>Active</Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell>
                                    <Input
                                        value={emailAddress}
                                        onChange={handleEmailAddressChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={firstName}
                                        onChange={handleFirstNameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={surname}
                                        onChange={handleSurnameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {customerUsers.map(customerUser => (
                                <Table.Row key={customerUser.id}>
                                    <Table.Cell>
                                        <Link href={`${path}/${customerUser.id}`}>
                                            {customerUser.emailAddress}
                                        </Link>
                                    </Table.Cell>

                                    <Table.Cell>
                                        <Link href={`${path}/${customerUser.id}`}>
                                            {customerUser.firstName}
                                        </Link>
                                    </Table.Cell>

                                    <Table.Cell>
                                        <Link href={`${path}/${customerUser.id}`}>
                                            {customerUser.surname}
                                        </Link>
                                    </Table.Cell>
                                    <TableCell>
                                        {customerUser.active !== null && customerUser.active !== undefined && customerUser.active.toString()}
                                    </TableCell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
